import React, { useState, useEffect, useRef } from "react";
import { Box, Checkbox, FormControlLabel, TextField } from "@mui/material";
import { SupportedUserDataTypes, type UserData } from "hooks/useUserDataKeys";
import { DateTimePicker } from "@mui/x-date-pickers";
import dayjs from "dayjs";
import type { FieldDefinition, FieldValue } from "model/CustomFields";
import CustomFieldInput from "components/custom-fields/CustomFieldInput";
import type { AttachmentHost } from "components/custom-fields/attachmentField";

interface UserfieldProps {
  name: string;
  value: unknown;
  onChange: (v: unknown) => void;
  type: UserData["type"];
  readonly?: boolean;
  linkChange?: boolean;
  /**
   * La definizione del contratto, quando il chiamante ce l'ha.
   *
   * Con questa si usa il renderer dei 15 tipi (`CustomFieldInput`); senza, resta
   * il percorso legacy a quattro tipi. Le due strade convivono perché le
   * superfici migrano una alla volta: una migrazione in blocco lascerebbe per
   * giorni un'applicazione a metà.
   */
  definition?: FieldDefinition | null;
  fieldId?: string;
  /** Come caricare un allegato, dove si può: vedi `AttachmentHost`. */
  attachments?: AttachmentHost | null;
}

const DATETIME_FORMAT = "DD-MM-YYYY hh:mm";

const Userfield: React.FC<UserfieldProps> = ({
  readonly,
  value: _value,
  linkChange = false,
  onChange,
  name,
  type,
  definition = null,
  fieldId,
  attachments = null,
}) => {
  const value = _value === "false" ? false : _value === "true" ? true : _value;
  const [internalValue, setInternalValue] = useState(value);
  const isEscaping = useRef(false);

  useEffect(() => {
    setInternalValue(value || "");
  }, [value]);

  const handleKeyUp = (e: React.KeyboardEvent) => {
    if (linkChange) return;

    if (e.key === "Enter") {
      onChange(internalValue);
      (e.target as HTMLElement).blur();
    } else if (e.key === "Escape") {
      isEscaping.current = true;
      setInternalValue(value || "");
      (e.target as HTMLElement).blur();
    }
  };

  const handleBlur = () => {
    if (linkChange) return;

    if (isEscaping.current) {
      isEscaping.current = false;
      return;
    }
    onChange(internalValue);
  };

  // Con la definizione si usa il renderer del contratto: conosce i 15 tipi e
  // le loro configurazioni, e non indovina nulla.
  if (definition) {
    return (
      <CustomFieldInput
        field={{
          fieldId: fieldId ?? definition.fieldId,
          definition,
          // L'accesso e' del contesto autorizzato, che qui non c'e': si usa
          // `readonly` come segnale, che e' cio' che il chiamante sa.
          access: readonly ? "readonly" : "editable",
          value: _value as FieldValue,
        }}
        value={_value as FieldValue}
        onChange={(v) => onChange(v)}
        attachments={attachments}
      />
    );
  }

  if (type === SupportedUserDataTypes.string) {
    return (
      <Box>
        <TextField
          fullWidth
          label={name}
          value={(linkChange ? value : internalValue) || ""}
          onChange={(e) =>
            linkChange
              ? onChange(e.target.value)
              : setInternalValue(e.target.value)
          }
          onBlur={handleBlur}
          onKeyUp={handleKeyUp}
          size="small"
          disabled={readonly}
          id={`string-userschema-${name}`}
          multiline
          maxRows={5}
        />
      </Box>
    );
  } else if (type === SupportedUserDataTypes.number) {
    return (
      <Box pt={1}>
        <TextField
          onChange={(e) => {
            linkChange
              ? onChange(e.target.value)
              : setInternalValue(e.target.value);
          }}
          fullWidth
          label={name}
          // Niente `|| 0`: un campo vuoto mostrava zero, e `false` e `0`
          // diventavano indistinguibili da "non compilato".
          value={(linkChange ? value : internalValue) ?? ""}
          onBlur={handleBlur}
          onKeyUp={handleKeyUp}
          size="small"
          // `type="number"` passerebbe per un `double`, arrotondando un importo
          // con piu' decimali di quanti ne stiano in un float.
          inputMode="decimal"
          disabled={readonly}
          id={`number-userschema-${name}`}
        />
      </Box>
    );
  } else if (type === SupportedUserDataTypes.boolean) {
    return (
      <Box pt={1}>
        <FormControlLabel
          control={
            <Checkbox
              size="small"
              checked={Boolean(value)}
              onChange={(e, checked) => {
                onChange(checked);
                e.stopPropagation();
              }}
              disabled={readonly}
              id={`boolean-userschema-${name}`}
            />
          }
          slotProps={{
            typography: {
              sx: {
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              },
            },
          }}
          sx={{ maxWidth: "100%", ml: 0, flex: 0 }}
          labelPlacement="start"
          label={name}
        />
      </Box>
    );
  } else if (type === SupportedUserDataTypes.date) {
    return (
      <Box pt={1}>
        <DateTimePicker
          value={value ? dayjs(value as string, DATETIME_FORMAT) : null}
          onChange={(value: dayjs.Dayjs | null) =>
            onChange(value?.format(DATETIME_FORMAT))
          }
          ampm={false}
          slotProps={{
            textField: {
              size: "small",
              fullWidth: true,
              label: name,
              id: `date-userschema-${name}`,
            },
          }}
          disabled={readonly}
        />
      </Box>
    );
  } else {
    return (
      <Box pt={1}>
        <TextField
          fullWidth
          label={name}
          value={(linkChange ? value : internalValue) || ""}
          onChange={(e) =>
            linkChange
              ? onChange(e.target.value)
              : setInternalValue(e.target.value)
          }
          onBlur={handleBlur}
          onKeyUp={handleKeyUp}
          size="small"
          disabled={readonly}
          id={`string-userschema-${name}`}
        />
      </Box>
    );
  }
};

export default Userfield;
